import { useCallback, useEffect, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3010';

export type UserRole = 'USER' | 'ADMIN';
export type UserStatus = 'ACTIVE' | 'SUSPENDED' | 'BANNED';

export interface User {
  id: string;
  email: string;
  username: string;
  country: string;
  locale: string;
  bio?: string | null;
  emailVerified: boolean;
  role: UserRole;
  status: UserStatus;
  createdAt: string;
}

interface AuthResponse<T = unknown> {
  success: boolean;
  message?: string;
  user?: User;
  data?: T;
}

class AuthError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public errors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

async function authFetch<T = AuthResponse>(endpoint: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}/auth${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers
    },
    credentials: 'include'
  });

  const data = await res.json();

  if (!res.ok) {
    throw new AuthError(data.message || 'Request failed', res.status, data.errors);
  }

  return data;
}

export const signIn = {
  email: (data: { email: string; password: string }) =>
    authFetch<AuthResponse>('/sign-in/email', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
  magicLink: (data: { email: string }) =>
    authFetch<AuthResponse>('/sign-in/magic-link', {
      method: 'POST',
      body: JSON.stringify(data)
    })
};

export const signUp = {
  email: (data: {
    email: string;
    password: string;
    username: string;
    country: string;
    privacyPolicyVersion: string;
  }) =>
    authFetch<AuthResponse>('/sign-up/email', {
      method: 'POST',
      body: JSON.stringify(data)
    })
};

export const signOut = () => authFetch<AuthResponse>('/sign-out', { method: 'POST' });

export const getSession = () => authFetch<{ user: User | null }>('/session');

export const verifyEmail = (token: string) =>
  authFetch<AuthResponse>(`/verify-email?token=${encodeURIComponent(token)}`);

export const verifyMagicLink = (token: string) =>
  authFetch<AuthResponse>(`/verify-magic-link?token=${encodeURIComponent(token)}`);

export const forgotPassword = (email: string) =>
  authFetch<AuthResponse>('/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email })
  });

export const resetPassword = (token: string, password: string) =>
  authFetch<AuthResponse>('/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password })
  });

export const changePassword = (currentPassword: string, newPassword: string) =>
  authFetch<AuthResponse>('/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword })
  });

export const resendVerification = () =>
  authFetch<AuthResponse>('/resend-verification', { method: 'POST' });

export const checkUsername = (username: string) =>
  authFetch<{ available: boolean }>(`/username-available/${encodeURIComponent(username)}`);

// React hook for session management
export function useSession() {
  const [data, setData] = useState<{ user: User } | null>(null);
  const [isPending, setIsPending] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSession = useCallback(async () => {
    try {
      const result = await getSession();
      setData(result.user ? { user: result.user } : null);
      setError(null);
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err : new Error('Failed to fetch session'));
    } finally {
      setIsPending(false);
    }
  }, []);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  return {
    data,
    isPending,
    error,
    refetch: fetchSession
  };
}
